import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(res => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = posts.find(p => p.featured);
  const regular = posts.filter(p => p.id !== featured?.id);

  return (
    <div data-testid="blog-page">
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Blog</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
            Insights &amp; Articles
          </h1>
          <p className="mt-4 text-base text-white/60 max-w-2xl">
            Faith-based perspectives on education, church schools, and family life.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-16 text-[#595959]">Loading articles...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#595959]">New articles coming soon. Stay tuned!</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featured && (
                <Link
                  to={`/blog/${featured.id}`}
                  data-testid="featured-post"
                  className="block mb-12 group"
                >
                  <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] overflow-hidden hover:shadow-lg transition-shadow">
                    {featured.featured_image && (
                      <img src={featured.featured_image} alt={featured.title} className="w-full h-64 object-cover" />
                    )}
                    <div className="p-8">
                      <div className="flex items-center gap-2 mb-3">
                        <Star className="w-4 h-4 text-[#C8A97E]" />
                        <span className="text-xs uppercase tracking-wider text-[#C8A97E] font-semibold">Featured</span>
                      </div>
                      <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1C2E4A] mb-3 group-hover:text-[#C8A97E] transition-colors">
                        {featured.title}
                      </h2>
                      <p className="text-sm text-[#595959] leading-relaxed mb-4">{featured.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-[#595959]/60">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(featured.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Post Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {regular.map((post, i) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    data-testid={`blog-card-${i}`}
                    className="group"
                  >
                    <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all h-full flex flex-col">
                      {post.featured_image && (
                        <img src={post.featured_image} alt={post.title} className="w-full h-44 object-cover" />
                      )}
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-2 group-hover:text-[#C8A97E] transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-sm text-[#595959] leading-relaxed flex-1 mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#595959]/60 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />{new Date(post.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-sm text-[#C8A97E] font-medium flex items-center gap-1">
                            Read More <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
